import {Avatar, Button, Input} from 'antd'
import {BsBuilding, BsCalendar3, BsCameraVideo, BsCheck2, BsGeoAlt, BsPencil, BsShieldLock,} from 'react-icons/bs'
import {BiSolidChurch} from 'react-icons/bi'
import classNames from 'classnames'
import {CustomToggleField} from 'components/form/fields/ToggleField'
import {createValue} from 'components/form/utils'
import LookupField from 'components/LookupField'
import {FormikContext} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useState} from 'react'
import {CustomDateAndTimeRangeField} from './components/EventOverviewSetting'
import {CustomInputTextAreaField} from './EventSetting'
import {EventTypes} from '../AppLocals/Event/EventContext'

// ─── Step indicator ───────────────────────────────────────────────────────────
// Compact progress dots + step title in one block, no extra subtitle

const StepIndicator = ({routes, currentIndex}) => {
    const t = useTranslate()
    return (
        <div className="mt-3 px-5 pt-5 pb-3">
            <div className="flex items-center mb-3">
                {routes.map((_, i) => (
                    <React.Fragment key={i}>
                        <div className={classNames(
                            'w-6 h-6 rounded-full flex flex-center text-xs font-bold border-2 transition-all duration-300 flex-shrink-0',
                            i < currentIndex
                                ? 'bg-secondary border-secondary text-white'
                                : i === currentIndex
                                    ? 'border-secondary text-secondary bg-white shadow-sm'
                                    : 'border-gray-200 text-gray-300 bg-white'
                        )}>
                            {i < currentIndex ? <BsCheck2 size={15}/> : <span>{i + 1}</span>}
                        </div>
                        {i < routes.length - 1 && (
                            <div
                                style={{ height: 1 }}
                                className={classNames(
                                    'flex-1 rounded-full transition-all duration-300',
                                    i < currentIndex ? 'bg-secondary' : 'bg-gray-200'
                                )}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
            <div className="flex flex-col items-center gap-1">
                <h2 className="text-center text-lg font-bold text-color-000 tracking-wide leading-snug">
                    {t(routes[currentIndex]?.title || '')}
                </h2>
                <p className="text-center font-semibold text-2xs text-color-400 uppercase tracking-widest leading-tight">
                    {t('step')} {currentIndex + 1}/{routes.length}
                </p>
            </div>
        </div>
    )
}

// ─── FieldSection ─────────────────────────────────────────────────────────────
// Label [required mark] → hint text → control → error
// Clear vertical grouping so user always knows which label owns which control

const FieldSection = ({icon, label, hint, required = false, error, children, className}) => {
    const t = useTranslate()
    return (
        <div className={classNames('flex flex-col gap-1', className)}>
            {label && (
                <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-color-200 tracking-wider">
                        {t(label)}
                    </span>
                    {required && (
                        <span className="text-red-400 text-xs leading-none font-bold">*</span>
                    )}
                </div>
            )}
            {hint && (
                <p className="text-xs text-color-400 italic tracking-wider leading-relaxed">
                    * {t(hint)}
                </p>
            )}
            {children}
            {error && (
                <span className="text-xs text-red-500 italic leading-snug">
                    {error}
                </span>
            )}
        </div>
    )
}

// ─── PreScreen ────────────────────────────────────────────────────────────────
// Pick event format — clicking a card auto-advances to step 2

const EVENT_TYPE_HINTS = {
    meeting: 'In-person gathering at a physical venue',
    webinar: 'Live online session via video conference',
    pilgrimage: 'Group journey or pilgrimage to a destination',
}

const PreScreen = ({onNext}) => {
    const t = useTranslate()
    const form = useContext(FormikContext)
    const selectedValue = form.values.event_type

    return (
        <div className="flex flex-col gap-4">
            <p className="text-center text-sm text-color-300 tracking-wider leading-relaxed">
                {t('Choose the format that best fits your event. This helps tailor the rest of the form.')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.values(EventTypes).map((e, i) => {
                    const isSelected = selectedValue === e?.value
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => {
                                form.setFieldValue('event_type', e.value)
                                onNext()
                            }}
                            className={classNames(
                                'px-3 py-5 w-full text-center flex flex-col items-center border rounded-xl cursor-pointer transition-all duration-200',
                                isSelected
                                    ? 'bg-secondary-50 text-secondary-600 border-secondary-600'
                                    : 'background-50 hover:bg-secondary-50 hover:border-secondary-400 hover:shadow-md'
                            )}>
                            <span
                                className={classNames('flex flex-center text-3xl', isSelected ? 'text-secondary-600' : 'text-gray-500')}>
                                {e.icon}
                            </span>
                            <span
                                className={classNames(
                                    'mb-1 text-base font-bold tracking-wide leading-snug',
                                    isSelected ? 'text-secondary-600' : 'text-color-000'
                                )}>
                                {t(e.label)}
                            </span>
                            {EVENT_TYPE_HINTS[e.value] && (
                                <span
                                    className={classNames('text-xs tracking-wider', isSelected ? 'text-secondary-500' : 'text-color-300')}>
                                    {t(EVENT_TYPE_HINTS[e.value])}
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

// ─── OwnerLookupField ─────────────────────────────────────────────────────────
// Searches only organizations the current user manages / is admin of

const OwnerLookupField = ({form}) => {
    const t = useTranslate()
    return (
        <LookupField
            name="owner_id"
            title={t('select owner')}
            createIfNotExists={false}
            placeholder={t('click to select')}
            defaultValue={form.values.owner_title || String(form.values.owner_id || '')}
            apiPath="/auth/managed-admin/organization/gets?filter_group=admin"
            renderOption={(o) => (
                <div className="flex items-center gap-2.5">
                    <Avatar
                        size={32}
                        src={o.avatar}
                        icon={<BiSolidChurch size={14} className="text-color-500"/>}
                        className="flex flex-center background-200 rounded-md flex-shrink-0"
                    />
                    <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-color-000 leading-snug truncate">
                            {o.label || o.title}
                        </span>
                        <span className="text-xs text-blue-500 italic leading-snug">
                            {t(o._type || o.type || 'organization')}
                        </span>
                    </div>
                </div>
            )}
            handleChange={(e) => {
                const sel = e.target
                form.setValues({
                    ...form.values,
                    owner_id: sel.id || sel.value,
                    owner_type: sel._type || sel.type || 'organization',
                    owner_title: sel.label || sel.title || '',
                })
            }}
        />
    )
}

// ─── FirstScreen ──────────────────────────────────────────────────────────────
// Name · Owner · Time · Privacy — core event details

const FirstScreen = () => {
    const t = useTranslate()
    const form = useContext(FormikContext)
    const titleError = form.errors.title

    return (
        <div className="flex flex-col gap-3">
            <FieldSection
                icon={<BsPencil size={11}/>}
                label="event name"
                required
                error={titleError}>
                <Input
                    size="large"
                    name="title"
                    value={form.values.title || ''}
                    status={titleError ? 'error' : undefined}
                    placeholder={t('eg: Sunday Mass, Youth Gathering, Bible Study...')}
                    onChange={form.handleChange}
                    className="rounded-lg"
                    autoFocus
                />
            </FieldSection>

            <FieldSection
                icon={<BsBuilding size={11}/>}
                label="owner"
                hint="The organization hosting this event. Search by name to select.">
                <OwnerLookupField form={form}/>
            </FieldSection>

            <FieldSection
                icon={<BsCalendar3 size={11}/>}
                label="time">
                <CustomDateAndTimeRangeField
                    onChange={form.handleChange}
                    form={form}
                    name="time_range"
                />
            </FieldSection>

            <FieldSection
                icon={<BsShieldLock size={11}/>}
                label="privacy">
                <CustomToggleField
                    name="privacy"
                    value={Number(form.values.privacy) === 2}
                    onChange={(checked) => form.setFieldValue('privacy', checked ? 2 : 1)}
                    title="private event"
                    description="private event description"
                />
            </FieldSection>
        </div>
    )
}

// ─── SecondScreen ─────────────────────────────────────────────────────────────
// Where: physical address for meetings/pilgrimages, or link for webinars

const SecondScreen = () => {
    const t = useTranslate()
    const form = useContext(FormikContext)
    const eventType = form.values.event_type || 'meeting'
    const isWebinar = eventType === 'webinar'

    return (
        <div className="flex flex-col space-y-10">
            {isWebinar ? (
                <FieldSection
                    icon={<BsCameraVideo size={11}/>}
                    label="online meeting link"
                    hint="Paste the video conference URL. Attendees will click this to join (Zoom, Google Meet, Teams, etc.).">
                    <Input
                        size="large"
                        name="external_link"
                        type="url"
                        value={form.values.external_link || ''}
                        placeholder="https://zoom.us/j/...  or  https://meet.google.com/..."
                        onChange={form.handleChange}
                        className="rounded-lg"
                        autoFocus
                    />
                </FieldSection>
            ) : (
                <FieldSection
                    icon={<BsGeoAlt size={11}/>}
                    label={eventType === 'pilgrimage' ? 'destination' : 'venue'}
                    hint={
                        eventType === 'pilgrimage'
                            ? 'Pilgrimage destination or departure gathering point.'
                            : 'Full address or name of the venue where the event takes place.'
                    }>
                    <Input
                        size="large"
                        name="location_name"
                        value={form.values.location_name || ''}
                        placeholder={t(
                            eventType === 'pilgrimage'
                                ? 'eg: La Vang Shrine, Marian Grotto...'
                                : 'eg: Church Hall, 123 Main Street, Room 201...'
                        )}
                        onChange={form.handleChange}
                        className="rounded-lg"
                        autoFocus
                    />
                </FieldSection>
            )}
        </div>
    )
}

// ─── ThirdScreen ──────────────────────────────────────────────────────────────
// Additional details — agenda, requirements, notes

const ThirdScreen = () => {
    const t = useTranslate()
    const form = useContext(FormikContext)

    return (
        <div className="flex flex-col space-y-10">
            <FieldSection
                icon={<BsPencil size={11}/>}
                label="description"
                hint="Help attendees prepare: agenda, attire, what to bring, registration instructions, or special notes.">
                <CustomInputTextAreaField
                    name="description"
                    size="large"
                    rows={6}
                    value={form.values.description || ''}
                    placeholder={t('Describe the program, schedule, and what attendees should prepare before joining...')}
                    onChange={form.handleChange}
                />
            </FieldSection>
        </div>
    )
}

// ─── Routes ───────────────────────────────────────────────────────────────────

const createEventFormRoutes = [
    {title: 'type of event', component: PreScreen},
    {title: 'basic information', component: FirstScreen},
    {title: 'location', component: SecondScreen},
    {title: 'details', component: ThirdScreen},
]

// ─── CreateEventForm ──────────────────────────────────────────────────────────
// Shell: step indicator → content → CTA footer

export default function CreateEventForm({
    asyncData: {isLoading},
    isValid,
    handleSubmit,
    ...props
}) {
    const t = useTranslate()
    const [currentIndex, setCurrentIndex] = useState(0)
    const isFirstStep = currentIndex === 0
    const isLastStep = currentIndex === createEventFormRoutes.length - 1

    const handleNext = () => {
        const nextIndex = Math.min(createEventFormRoutes.length - 1, currentIndex + 1)
        props.handleChange(createValue('currentIndex', nextIndex))
        setCurrentIndex(nextIndex)
        if (isValid && isLastStep) {
            handleSubmit()
        }
    }

    const handleBack = () => {
        setCurrentIndex(Math.max(0, currentIndex - 1))
    }

    const C = createEventFormRoutes[currentIndex].component

    return (
        <div className="flex flex-col">
            <StepIndicator routes={createEventFormRoutes} currentIndex={currentIndex}/>

            {/* Screen content — scrollable if content overflows */}
            <div className="px-5">
                <C isLoading={isLoading} onNext={handleNext} onBack={handleBack}/>
            </div>

            {/* CTA footer — back is text, next/submit is prominent */}
            <div className="mt-6 px-5 py-3 w-full flex flex-center gap-3">
                {!isFirstStep && (
                    <Button
                        type="text"
                        loading={isLoading}
                        className="text-color-400 hover:text-color-000"
                        onClick={handleBack}>
                        ← {t('back')}
                    </Button>
                )}
                <FormikContext.Consumer>
                    {({errors}) => (
                        <Button
                            size="large"
                            loading={isLoading}
                            disabled={!_.isEmpty(errors)}
                            className="px-8 border-0 flex flex-center font-medium tracking-wide rounded-xl"
                            color="primary" variant="filled"
                            onClick={handleNext}>
                            {isLastStep ? `${t('create event')}` : `${t('continue')} →`}
                        </Button>
                    )}
                </FormikContext.Consumer>
            </div>
        </div>
    )
}
